import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  type RefObject,
} from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  RichTextContent,
} from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
import type { Amenity } from '@vibesites/schema';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ============================================================================
// Inlined helpers (parent theme doesn't export these hooks / GlowTrace)
// ============================================================================

function useScrollReveal<T extends HTMLElement = HTMLElement>(
  options?: { threshold?: number; rootMargin?: string },
): RefObject<T> {
  const ref = useRef<T>(null) as RefObject<T>;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    el.classList.add('reveal--hidden');

    const raf = requestAnimationFrame(() => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            el.classList.remove('reveal--hidden');
            el.classList.add('reveal--visible');
            observer.unobserve(el);
          }
        },
        {
          threshold: options?.threshold ?? 0.15,
          rootMargin: options?.rootMargin ?? '0px 0px -40px 0px',
        },
      );
      observer.observe(el);
    });

    return () => cancelAnimationFrame(raf);
  }, [options?.threshold, options?.rootMargin]);

  return ref;
}

const REVEAL_STYLE_ID = 'avenue-reveal-animations';

function useRevealStyles(): void {
  useEffect(() => {
    if (document.getElementById(REVEAL_STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = REVEAL_STYLE_ID;
    style.textContent = `
      .reveal-fade-up.reveal--hidden { opacity: 0; transform: translateY(24px); }
      .reveal-fade-up.reveal--visible {
        opacity: 1; transform: translateY(0);
        transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1),
                    transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
      }
      .accent-line { overflow: visible; }
      .accent-line line, .accent-line path {
        stroke: var(--vibe-color-primary);
        stroke-width: 1;
        fill: none;
        stroke-dasharray: var(--line-length, 200);
        stroke-dashoffset: var(--line-length, 200);
        opacity: 0;
      }
      .reveal--visible .accent-line line,
      .reveal--visible .accent-line path,
      .accent-line.reveal--visible line,
      .accent-line.reveal--visible path {
        opacity: 1;
        stroke-dashoffset: 0;
        transition: stroke-dashoffset 1.2s cubic-bezier(0.22, 1, 0.36, 1) 0.3s,
                    opacity 0.4s ease 0.3s;
      }
      .reveal--visible .accent-line--delay-1 line,
      .reveal--visible .accent-line--delay-1 path {
        transition-delay: 0.4s;
      }
      .glow-trace-wrap { position: relative; }
      .glow-trace-svg { position: absolute; inset: 0; width: 100%; height: 100%; z-index: 20; pointer-events: none; overflow: visible; }
      .glow-trace-svg rect {
        fill: none; stroke: var(--vibe-color-primary); stroke-width: 2; rx: 2; ry: 2;
        filter: drop-shadow(0 0 4px var(--vibe-color-primary))
                drop-shadow(0 0 8px color-mix(in srgb, var(--vibe-color-primary) 40%, transparent));
      }
      @media (prefers-reduced-motion: reduce) {
        .reveal-fade-up.reveal--hidden { opacity: 1; transform: none; }
        .accent-line line, .accent-line path { stroke-dashoffset: 0; opacity: 1; }
        .glow-trace-svg rect { opacity: 0 !important; animation: none !important; }
      }
    `;
    document.head.appendChild(style);
  }, []);
}

function GlowTrace({ delay = 0.3 }: { delay?: number }) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const rectRef = useRef<SVGRectElement>(null);
  const [dims, setDims] = useState({ w: 0, h: 0, perim: 0 });
  const hasAnimated = useRef(false);

  const measure = useCallback(() => {
    const el = wrapRef.current?.parentElement;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    if (width > 0 && height > 0) {
      setDims({ w: width, h: height, perim: 2 * (width + height) });
    }
  }, []);

  useEffect(() => {
    const el = wrapRef.current?.parentElement;
    if (!el) return;
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  useEffect(() => {
    if (dims.perim === 0 || hasAnimated.current) return;
    const rect = rectRef.current;
    const wrap = wrapRef.current;
    if (!rect || !wrap) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const target = wrap.parentElement || wrap;

    const runAnimation = () => {
      if (hasAnimated.current) return;
      hasAnimated.current = true;

      rect.style.strokeDasharray = `${dims.perim}`;
      rect.style.strokeDashoffset = `${dims.perim}`;
      rect.style.opacity = '0';

      rect.animate(
        [
          { strokeDashoffset: `${dims.perim}`, opacity: 0 },
          { strokeDashoffset: `${dims.perim}`, opacity: 0.85, offset: 0.04 },
          { strokeDashoffset: `${dims.perim * 0.08}`, opacity: 0.85, offset: 0.72 },
          { strokeDashoffset: '0', opacity: 0 },
        ],
        {
          duration: 2200,
          easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
          delay: delay * 1000,
          fill: 'forwards',
        },
      );
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          runAnimation();
        }
      },
      { threshold: 0.3, rootMargin: '0px 0px -40px 0px' },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [dims.perim, delay]);

  const inset = 1;
  const ready = dims.perim > 0;

  return (
    <div
      ref={wrapRef}
      style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 20, overflow: 'visible' }}
    >
      {ready && (
        <svg className="glow-trace-svg" viewBox={`0 0 ${dims.w} ${dims.h}`} preserveAspectRatio="none">
          <rect
            ref={rectRef}
            x={inset}
            y={inset}
            width={dims.w - inset * 2}
            height={dims.h - inset * 2}
            style={{ opacity: 0 }}
          />
        </svg>
      )}
    </div>
  );
}

// ============================================================================
// Types for collection-bound gallery images
// ============================================================================

interface OverviewGalleryImage {
  image: string;
  alt_text?: string;
  title?: string;
  description?: string;
}

const OVERVIEW_AMENITY_LIMIT = 5;

// ============================================================================
// Slot Group Definitions
// ============================================================================

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'See All Availability' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Image Slider Sub-component
// ============================================================================

function GallerySlider({ photos }: { photos: OverviewGalleryImage[] }) {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const count = photos.length;

  const goTo = useCallback((idx: number) => {
    if (isTransitioning || count <= 1) return;
    setIsTransitioning(true);
    setCurrent(idx);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning, count]);

  const next = useCallback(() => goTo((current + 1) % count), [current, count, goTo]);
  const prev = useCallback(() => goTo((current - 1 + count) % count), [current, count, goTo]);

  useEffect(() => {
    if (count <= 1) return;
    timerRef.current = setTimeout(next, 5000);
    return () => clearTimeout(timerRef.current);
  }, [current, count, next]);

  if (!count) return <div className="w-full h-full bg-[var(--vibe-color-surface)]" />;

  return (
    <div className="relative w-full h-full overflow-hidden rounded-sm group">
      {photos.map((photo, i) => (
        <VibesitesImage
          key={i}
          url={photo.image || ''}
          alt={photo.alt_text || photo.title || ''}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-600 ease-in-out ${
            i === current ? 'opacity-100' : 'opacity-0'
          }`}
        />
      ))}

      {/* Brand-tinted overlay at bottom for depth */}
      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/30 to-transparent pointer-events-none" />

      {count > 1 && (
        <>
          {/* Prev / Next arrows */}
          <button
            onClick={prev}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-white/80 text-[var(--vibe-color-text)] opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-white"
            aria-label="Previous image"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
            </svg>
          </button>
          <button
            onClick={next}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-white/80 text-[var(--vibe-color-text)] opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-white"
            aria-label="Next image"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
            </svg>
          </button>

          {/* Dots */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
            {photos.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  i === current ? 'bg-white w-5' : 'bg-white/50 hover:bg-white/80'
                }`}
                aria-label={`Go to image ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function PropertyOverview() {
  const description = useContentToken('propertyoverview.description');
  const ctaLink = useSlotGroup('propertyoverview.cta_link', ctaLinkDef);
  const tabInteriorLabel = useContentToken('propertyoverview.tab_interior_label');
  const tabCommunityLabel = useContentToken('propertyoverview.tab_community_label');

  // Use useCollectionBinding instead of useGallery so the image slider
  // is editable in the Vibesites content editor (hide, reorder, add custom images).
  const galleryImages = useCollectionBinding<OverviewGalleryImage>('propertyoverview.gallery_images', {
    collection: 'photo_gallery',
    defaultLimit: 12,
  });
  const apartmentAmenities = useCollectionBinding<Amenity>('propertyoverview.apartment_amenities', {
    collection: 'amenities',
    filter: { group: 'Apartment' },
    defaultLimit: OVERVIEW_AMENITY_LIMIT,
  });
  const communityAmenities = useCollectionBinding<Amenity>('propertyoverview.community_amenities', {
    collection: 'amenities',
    filter: { group: 'Community' },
    defaultLimit: OVERVIEW_AMENITY_LIMIT,
  });

  const sliderPhotos = useMemo(
    () => (galleryImages || []).filter((p) => p.image),
    [galleryImages],
  );

  const [activeTab, setActiveTab] = useState<'interior' | 'community'>('interior');
  const [descExpanded, setDescExpanded] = useState(false);
  const activeItems = activeTab === 'interior' ? apartmentAmenities : communityAmenities;

  useRevealStyles();
  const accentRef = useScrollReveal<HTMLDivElement>({ threshold: 0.2 });

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">

          {/* ============================================================ */}
          {/* Left Column: Text + Amenity Tabs                              */}
          {/* ============================================================ */}
          <div ref={accentRef as RefObject<HTMLDivElement>} className="reveal-fade-up">
            {/* Accent line draws in above the eyebrow */}
            <svg className="accent-line accent-line--delay-1 mb-4" style={{ '--line-length': '64', width: '64px', height: '3px' } as React.CSSProperties} viewBox="0 0 64 3" preserveAspectRatio="none">
              <line x1="0" y1="1.5" x2="64" y2="1.5" strokeWidth="2" />
            </svg>

            <RichTextContent
              path="propertyoverview.pre_title"
              label="Eyebrow Text"
              defaultValue="<p>Your Search Ends Here</p>"
              as="p"
              className="font-body text-xs sm:text-sm uppercase tracking-[0.2em] text-[var(--vibe-color-primary)] mb-3"
            />

            <RichTextContent
              path="propertyoverview.title"
              label="Title"
              defaultValue="<p>Experience the charm.</p>"
              as="h2"
              className="font-heading text-3xl sm:text-4xl md:text-[2.75rem] font-normal text-[var(--vibe-color-text)] mb-5 leading-[1.15]"
            />

            <div className="mb-10 max-w-lg">
              <div className={`overview-desc-wrap ${descExpanded ? 'overview-desc-wrap--open' : ''}`}>
                <RichTextContent
                  path="propertyoverview.description"
                  label="Description"
                  defaultValue="<p>Welcome to our residential community featuring spacious layouts and thoughtful amenities. Discover the perfect blend of comfort, style, and convenience.</p>"
                  as="div"
                  className="font-body text-sm sm:text-base text-[var(--vibe-color-text-muted)] leading-relaxed"
                />
              </div>
              {description && String(description).length > 200 && (
                <button
                  onClick={() => setDescExpanded(!descExpanded)}
                  className="mt-2 font-body text-sm font-semibold text-[var(--vibe-color-text)] hover:underline transition-colors"
                >
                  {descExpanded ? (
                    <RichTextContent
                      path="propertyoverview.read_less_label"
                      label="Read Less Label"
                      defaultValue="<p>Read Less</p>"
                      as="span"
                    />
                  ) : (
                    <RichTextContent
                      path="propertyoverview.read_more_label"
                      label="Read More Label"
                      defaultValue="<p>Read More</p>"
                      as="span"
                    />
                  )}
                </button>
              )}
            </div>

            {/* Amenity Tabs */}
            {(apartmentAmenities.length > 0 || communityAmenities.length > 0) && (
              <div className="mb-8">
                <div className="overview-tab-row flex gap-2">
                  {apartmentAmenities.length > 0 && (
                    <button
                      onClick={() => setActiveTab('interior')}
                      className={`overview-tab ${activeTab === 'interior' ? 'overview-tab--active' : ''}`}
                    >
                      <EditableToken path="propertyoverview.tab_interior_label" defaultValue="Interior Features" as="span">
                        {tabInteriorLabel}
                      </EditableToken>
                    </button>
                  )}
                  {communityAmenities.length > 0 && (
                    <button
                      onClick={() => setActiveTab('community')}
                      className={`overview-tab ${activeTab === 'community' ? 'overview-tab--active' : ''}`}
                    >
                      <EditableToken path="propertyoverview.tab_community_label" defaultValue="Community Amenities" as="span">
                        {tabCommunityLabel}
                      </EditableToken>
                    </button>
                  )}
                </div>

                <div className="pt-5">
                  <ul className="space-y-2.5">
                    {activeItems.map((a, i) => (
                      <li key={`${activeTab}-${i}`} className="flex items-center gap-3 text-sm font-body text-[var(--vibe-color-text-muted)]">
                        <span className="overview-bullet flex-shrink-0" />
                        {a.title}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* CTA */}
            <EditableSlotGroup groupPath="propertyoverview.cta_link" definition={ctaLinkDef}>
              <a
                href={ctaLink.url || '/floor-plans'}
                target={ctaLink._target || '_self'}
                className="overview-cta-btn inline-flex items-center gap-2"
              >
                <span>{ctaLink.label || 'See All Availability'}</span>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                </svg>
              </a>
            </EditableSlotGroup>
          </div>

          {/* ============================================================ */}
          {/* Right Column: Gallery Slider with corner accents              */}
          {/* ============================================================ */}
          <div className="glow-trace-wrap relative w-full aspect-[4/3] lg:aspect-[3/4] lg:max-h-[640px] rounded-sm overflow-hidden">
            <GlowTrace delay={0.5} />
            <GallerySlider photos={sliderPhotos} />
          </div>
        </div>
      </div>

      <style>{`
        /* Description expand / collapse */
        .overview-desc-wrap {
          max-height: 9.6em;
          overflow: hidden;
          transition: max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          -webkit-mask-image: linear-gradient(to bottom, black 60%, transparent 100%);
          mask-image: linear-gradient(to bottom, black 60%, transparent 100%);
        }
        .overview-desc-wrap--open {
          max-height: 60em;
          -webkit-mask-image: none;
          mask-image: none;
        }

        /* Tabs — AMC override: transparent background in all states.
           Border + text color still communicate the active tab. */
        .overview-tab {
          padding: 0.5rem 1.25rem;
          font-size: 0.6875rem;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: var(--vibe-color-text-muted);
          background: transparent;
          border: 1px solid var(--vibe-color-border);
          border-radius: 9999px;
          transition: color 0.3s ease, border-color 0.3s ease;
          cursor: pointer;
        }
        .overview-tab:hover {
          color: var(--vibe-color-primary);
          background: transparent;
          border-color: var(--vibe-color-primary);
        }
        .overview-tab--active {
          color: var(--vibe-color-primary);
          background: transparent;
          border-color: var(--vibe-color-primary);
          box-shadow: none;
        }
        .overview-tab--active:hover {
          color: var(--vibe-color-primary);
          border-color: var(--vibe-color-primary);
        }

        /* Bullet */
        .overview-bullet {
          display: block;
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: var(--vibe-color-primary);
          opacity: 0.6;
          flex-shrink: 0;
        }

        /* CTA Button — transparent with border outline */
        .overview-cta-btn {
          padding: 0.875rem 2rem;
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--vibe-color-text);
          background: transparent;
          border: 1px solid var(--vibe-color-text);
          border-radius: 9999px;
          transition: all 0.3s ease;
        }
        .overview-cta-btn:hover {
          transform: translateY(-1px);
          color: var(--vibe-color-primary);
          border-color: var(--vibe-color-primary);
          background: transparent;
        }

        /* Slider transition */
        .duration-600 {
          transition-duration: 600ms;
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyOverview',
  displayName: 'Property Overview',
  tokens: {
    'propertyoverview.pre_title': {
      label: 'Eyebrow Text',
      type: 'richtext',
      defaultValue: '<p>Your Search Ends Here</p>',
    },
    'propertyoverview.title': {
      label: 'Title',
      type: 'richtext',
      defaultField: 'property_home_hero_title',
      defaultValue: '<p>Experience the charm.</p>',
    },
    'propertyoverview.description': {
      label: 'Description',
      type: 'richtext',
      defaultField: 'property_long_description',
      defaultValue: '<p>Welcome to our residential community featuring spacious layouts and thoughtful amenities.</p>',
    },
    'propertyoverview.tab_interior_label': {
      label: 'Interior Tab Label',
      type: 'text',
      defaultValue: 'Interior Features',
    },
    'propertyoverview.tab_community_label': {
      label: 'Community Tab Label',
      type: 'text',
      defaultValue: 'Community Amenities',
    },
    'propertyoverview.read_more_label': {
      label: 'Read More Label',
      type: 'richtext',
      defaultValue: '<p>Read More</p>',
    },
    'propertyoverview.read_less_label': {
      label: 'Read Less Label',
      type: 'richtext',
      defaultValue: '<p>Read Less</p>',
    },
  },
  slotGroups: {
    'propertyoverview.cta_link': {
      label: 'CTA Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
        label: { label: 'Button Text', type: 'text', defaultValue: 'See All Availability' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
  },
  collectionBindings: {
    'propertyoverview.gallery_images': {
      label: 'Overview Slideshow Images',
      collection: 'photo_gallery',
      defaultLimit: 12,
    },
    'propertyoverview.apartment_amenities': {
      label: 'Apartment Amenities',
      collection: 'amenities',
      filter: { group: 'Apartment' },
      defaultLimit: 5,
    },
    'propertyoverview.community_amenities': {
      label: 'Community Amenities',
      collection: 'amenities',
      filter: { group: 'Community' },
      defaultLimit: 5,
    },
  },
};

(PropertyOverview as any).contentSchema = contentSchema;

export default PropertyOverview;
